/**
 * Library form specs — drives the shared FormModal primitive for the
 * "Add / Edit X" flow on each library tab.
 *
 * One spec per entity type:
 *   fields(referenceData)  → FormModal `fields` array (multi-select
 *                            options closed over the loaded refs).
 *   initialValues(item)    → flat object FormModal seeds the form with.
 *   toPayload(values)      → entity payload `{ name, description,
 *                            entity_type, entity_data }` POSTed/PUTed
 *                            via libraryService.
 *
 * Threat's spec lives in ./threatFormSpec.js (its live feasibility-score
 * preview uses FormModal's `custom` field type); it's registered below.
 */
import { THREAT_SPEC } from './threatFormSpec';

const SECURITY_PROPERTIES_6 = [
  'Confidentiality',
  'Integrity',
  'Availability',
  'Authenticity',
  'Non-repudiation',
  'Authorization',
];

const SECURITY_PROPERTIES_4 = [
  'Confidentiality',
  'Integrity',
  'Availability',
  'Authenticity',
];

const IMPACT_RATINGS = ['Severe', 'Major', 'Moderate', 'Negligible'];
const CAL_LEVELS = ['CAL1', 'CAL2', 'CAL3', 'CAL4'];

const CONTROL_TYPES = [
  'Cryptographic',
  'Access Control',
  'Secure Communication',
  'Firewall',
  'IDS/IPS',
  'Authentication',
  'Secure Boot',
  'Code Signing',
  'Hardening',
  'Monitoring',
  'Other',
];

const ATTACK_VECTORS = ['Physical', 'Local', 'Adjacent', 'Network'];
const ELAPSED_TIMES = ['<1 day', '<1 week', '<1 month', '<6 months', '>6 months'];
const EXPERTISE_LEVELS = ['Layman', 'Proficient', 'Expert', 'Multiple experts'];
const KNOWLEDGE_LEVELS = ['Public', 'Restricted', 'Confidential', 'Strictly confidential'];
const WINDOW_OPTIONS = ['Unlimited', 'Easy', 'Moderate', 'Difficult/none'];
const EQUIPMENT_OPTIONS = ['Standard', 'Specialized', 'Bespoke', 'Multiple bespoke'];
const FEASIBILITY_RATINGS = ['High', 'Medium', 'Low', 'Very Low'];

// ── Helpers ──────────────────────────────────────────────────────────

const toOption = (value, label = value) => ({ value, label });
const enumOptions = (values) => values.map((v) => toOption(v));
const refOptions = (refs, labelFn = (r) => r.name) =>
  (refs || []).map((r) => ({ value: r.item_id, label: labelFn(r) }));

// Multi-line textarea → array, dropping blank lines. Used by Feature's
// "Functional Requirements" and AttackPath's "Preconditions" fields.
const splitLines = (text) =>
  String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

const joinLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

// Common helper: name + description fields shared across most specs.
const namedFields = (entityLabel) => [
  {
    name: 'name',
    type: 'text',
    label: `${entityLabel} Name`,
    required: true,
    autoFocus: true,
  },
  {
    name: 'description',
    type: 'textarea',
    label: 'Description',
    required: true,
    rows: 3,
  },
];

// ── Specs ────────────────────────────────────────────────────────────

const MODULE_SPEC = {
  label: 'Module',
  fields: (refs) => [
    ...namedFields('Module'),
    { name: 'purpose', type: 'textarea', label: 'Purpose', required: true, rows: 3 },
    {
      name: 'featureRefs',
      type: 'multi-select',
      label: 'Features in this Module',
      options: refOptions(refs.features),
    },
    {
      name: 'assetRefs',
      type: 'multi-select',
      label: 'Assets in this Module',
      options: refOptions(refs.assets, (r) =>
        `${r.name}${r.entity_data?.type ? ` (${r.entity_data.type})` : ''}`),
    },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    purpose: item?.entity_data?.purpose || '',
    featureRefs: item?.entity_data?.featureRefs || [],
    assetRefs: item?.entity_data?.assetRefs || [],
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'module',
    entity_data: {
      purpose: v.purpose,
      featureRefs: v.featureRefs,
      assetRefs: v.assetRefs,
    },
  }),
};

const FEATURE_SPEC = {
  label: 'Feature',
  fields: (refs) => [
    ...namedFields('Feature'),
    {
      name: 'moduleRefs',
      type: 'multi-select',
      label: 'Belongs to Modules',
      options: refOptions(refs.modules),
    },
    {
      name: 'assetRefs',
      type: 'multi-select',
      label: 'Assets in this Feature',
      options: refOptions(refs.assets, (r) =>
        `${r.name}${r.entity_data?.type ? ` (${r.entity_data.type})` : ''}`),
    },
    {
      name: 'functionalRequirements',
      type: 'textarea',
      label: 'Functional Requirements (one per line)',
      rows: 5,
      placeholder: 'Enter each requirement on a new line',
    },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    moduleRefs: item?.entity_data?.moduleRefs || [],
    assetRefs: item?.entity_data?.assetRefs || [],
    functionalRequirements: joinLines(item?.entity_data?.functionalRequirements),
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'feature',
    entity_data: {
      moduleRefs: v.moduleRefs,
      assetRefs: v.assetRefs,
      functionalRequirements: splitLines(v.functionalRequirements),
    },
  }),
};

const ASSET_SPEC = {
  label: 'Asset',
  fields: (refs) => [
    ...namedFields('Asset'),
    { name: 'type', type: 'text', label: 'Asset Type', required: true, defaultValue: 'ECU' },
    {
      name: 'subtype',
      type: 'text',
      label: 'Asset Subtype',
      placeholder: 'e.g., Gateway ECU, Temperature Sensor',
      helpText: 'Specific role or implementation of the asset type',
    },
    {
      name: 'subtypeOther',
      type: 'text',
      label: 'Custom Subtype Description',
      placeholder: 'Describe the custom subtype',
      helpText: 'Only used when Subtype is set to "Other".',
    },
    {
      name: 'securityProperties',
      type: 'checkbox-group',
      label: 'Security Properties',
      required: true,
      options: enumOptions(SECURITY_PROPERTIES_6),
      validate: (v) => (Array.isArray(v) && v.length > 0 ? null : 'Select at least one security property'),
    },
    {
      name: 'featureRefs',
      type: 'multi-select',
      label: 'Belongs to Features',
      options: refOptions(refs.features),
    },
    {
      name: 'moduleRefs',
      type: 'multi-select',
      label: 'Belongs to Modules',
      options: refOptions(refs.modules),
    },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    type: item?.entity_data?.type || 'ECU',
    subtype: item?.entity_data?.subtype || '',
    subtypeOther: item?.entity_data?.subtypeOther || '',
    securityProperties: item?.entity_data?.securityProperties || [],
    featureRefs: item?.entity_data?.featureRefs || [],
    moduleRefs: item?.entity_data?.moduleRefs || [],
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'asset',
    entity_data: {
      type: v.type,
      subtype: v.subtype || null,
      subtypeOther: v.subtype === 'Other' ? (v.subtypeOther || null) : null,
      securityProperties: v.securityProperties,
      featureRefs: v.featureRefs,
      moduleRefs: v.moduleRefs,
    },
  }),
};

const DAMAGE_SCENARIO_SPEC = {
  label: 'Damage Scenario',
  fields: (refs) => [
    ...namedFields('Scenario'),
    {
      name: 'assetRefs',
      type: 'multi-select',
      label: 'Affected Assets',
      options: refOptions(refs.assets),
    },
    {
      name: 'compromisedProperties',
      type: 'checkbox-group',
      label: 'Compromised Properties',
      required: true,
      options: enumOptions(SECURITY_PROPERTIES_6),
      validate: (v) => (Array.isArray(v) && v.length > 0 ? null : 'Select at least one property'),
    },
    { name: 'safetyImpact',      type: 'select', label: 'Safety Impact',       required: true, width: 'half', options: enumOptions(IMPACT_RATINGS), defaultValue: 'Moderate' },
    { name: 'financialImpact',   type: 'select', label: 'Financial Impact',    required: true, width: 'half', options: enumOptions(IMPACT_RATINGS), defaultValue: 'Moderate' },
    { name: 'operationalImpact', type: 'select', label: 'Operational Impact',  required: true, width: 'half', options: enumOptions(IMPACT_RATINGS), defaultValue: 'Moderate' },
    { name: 'privacyImpact',     type: 'select', label: 'Privacy Impact',      required: true, width: 'half', options: enumOptions(IMPACT_RATINGS), defaultValue: 'Moderate' },
    { name: 'finalImpactRating', type: 'select', label: 'Final Impact Rating', required: true, options: enumOptions(IMPACT_RATINGS), defaultValue: 'Moderate' },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    assetRefs: item?.entity_data?.assetRefs || [],
    compromisedProperties: item?.entity_data?.compromisedProperties || [],
    safetyImpact: item?.entity_data?.safetyImpact || 'Moderate',
    financialImpact: item?.entity_data?.financialImpact || 'Moderate',
    operationalImpact: item?.entity_data?.operationalImpact || 'Moderate',
    privacyImpact: item?.entity_data?.privacyImpact || 'Moderate',
    finalImpactRating: item?.entity_data?.finalImpactRating || 'Moderate',
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'damage_scenario',
    entity_data: {
      assetRefs: v.assetRefs,
      compromisedProperties: v.compromisedProperties,
      safetyImpact: v.safetyImpact,
      financialImpact: v.financialImpact,
      operationalImpact: v.operationalImpact,
      privacyImpact: v.privacyImpact,
      finalImpactRating: v.finalImpactRating,
    },
  }),
};

const ATTACK_PATH_SPEC = {
  label: 'Attack Path',
  fields: (refs) => [
    ...namedFields('Attack Path'),
    {
      name: 'threatRefs',
      type: 'multi-select',
      label: 'Threat References',
      options: refOptions(refs.threats),
    },
    { name: 'attackVector', type: 'select', label: 'Attack Vector', required: true, options: enumOptions(ATTACK_VECTORS), defaultValue: 'Network' },
    {
      name: 'threatAgent',
      type: 'text',
      label: 'Threat Agent',
      required: true,
      placeholder: 'e.g., Skilled attacker with automotive knowledge',
    },
    {
      name: 'preconditions',
      type: 'textarea',
      label: 'Preconditions (one per line)',
      rows: 3,
      placeholder: 'Enter each precondition on a new line',
    },
    { name: 'elapsedTime',         type: 'select', label: 'Elapsed Time',          required: true, width: 'half', options: enumOptions(ELAPSED_TIMES),     defaultValue: '<1 week' },
    { name: 'specialistExpertise', type: 'select', label: 'Specialist Expertise',  required: true, width: 'half', options: enumOptions(EXPERTISE_LEVELS),  defaultValue: 'Proficient' },
    { name: 'knowledgeOfItem',     type: 'select', label: 'Knowledge of Item',     required: true, width: 'half', options: enumOptions(KNOWLEDGE_LEVELS),  defaultValue: 'Public' },
    { name: 'windowOfOpportunity', type: 'select', label: 'Window of Opportunity', required: true, width: 'half', options: enumOptions(WINDOW_OPTIONS),    defaultValue: 'Moderate' },
    { name: 'equipment',           type: 'select', label: 'Equipment',             required: true, width: 'half', options: enumOptions(EQUIPMENT_OPTIONS), defaultValue: 'Standard' },
    { name: 'attackFeasibilityRating', type: 'select', label: 'Attack Feasibility Rating', required: true, width: 'half', options: enumOptions(FEASIBILITY_RATINGS), defaultValue: 'Medium' },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    threatRefs: item?.entity_data?.threatRefs || [],
    attackVector: item?.entity_data?.attackVector || 'Network',
    threatAgent: item?.entity_data?.threatAgent || '',
    preconditions: joinLines(item?.entity_data?.preconditions),
    elapsedTime: item?.entity_data?.elapsedTime || '<1 week',
    specialistExpertise: item?.entity_data?.specialistExpertise || 'Proficient',
    knowledgeOfItem: item?.entity_data?.knowledgeOfItem || 'Public',
    windowOfOpportunity: item?.entity_data?.windowOfOpportunity || 'Moderate',
    equipment: item?.entity_data?.equipment || 'Standard',
    attackFeasibilityRating: item?.entity_data?.attackFeasibilityRating || 'Medium',
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'attack_path',
    entity_data: {
      threatRefs: v.threatRefs,
      attackVector: v.attackVector,
      threatAgent: v.threatAgent,
      preconditions: splitLines(v.preconditions),
      elapsedTime: v.elapsedTime,
      specialistExpertise: v.specialistExpertise,
      knowledgeOfItem: v.knowledgeOfItem,
      windowOfOpportunity: v.windowOfOpportunity,
      equipment: v.equipment,
      attackFeasibilityRating: v.attackFeasibilityRating,
    },
  }),
};

const GOAL_SPEC = {
  label: 'Cybersecurity Goal',
  fields: (refs) => [
    {
      name: 'goalStatement',
      type: 'textarea',
      label: 'Goal Statement',
      required: true,
      autoFocus: true,
      rows: 3,
      placeholder: 'e.g., Ensure integrity and authenticity of all CAN bus messages',
    },
    { name: 'securityProperty', type: 'select', label: 'Security Property', required: true, width: 'half', options: enumOptions(SECURITY_PROPERTIES_4), defaultValue: 'Integrity' },
    { name: 'calLevel',         type: 'select', label: 'CAL Level',         required: true, width: 'half', options: enumOptions(CAL_LEVELS),           defaultValue: 'CAL2' },
    {
      name: 'assetRefs',
      type: 'multi-select',
      label: 'Asset References',
      options: refOptions(refs.assets),
    },
  ],
  initialValues: (item) => ({
    goalStatement: item?.entity_data?.goalStatement || '',
    securityProperty: item?.entity_data?.securityProperty || 'Integrity',
    calLevel: item?.entity_data?.calLevel || 'CAL2',
    assetRefs: item?.entity_data?.assetRefs || [],
  }),
  toPayload: (v) => ({
    name: v.goalStatement.substring(0, 100),
    description: v.goalStatement,
    entity_type: 'goal',
    entity_data: {
      goalStatement: v.goalStatement,
      securityProperty: v.securityProperty,
      calLevel: v.calLevel,
      assetRefs: v.assetRefs,
    },
  }),
};

const CLAIM_SPEC = {
  label: 'Cybersecurity Claim',
  fields: (refs) => [
    {
      name: 'claimStatement',
      type: 'textarea',
      label: 'Claim Statement',
      required: true,
      autoFocus: true,
      rows: 3,
      placeholder: 'State the security claim...',
    },
    {
      name: 'justification',
      type: 'textarea',
      label: 'Justification',
      required: true,
      rows: 4,
      placeholder: 'Provide justification for this claim...',
    },
    {
      name: 'riskScenario',
      type: 'textarea',
      label: 'Risk Scenario',
      required: true,
      rows: 3,
      placeholder: 'Describe the related risk scenario...',
    },
    {
      name: 'goalRefs',
      type: 'multi-select',
      label: 'Related Goals',
      options: refOptions(refs.goals, (r) => r.entity_data?.goalStatement || r.name || 'Untitled Goal'),
    },
  ],
  initialValues: (item) => ({
    claimStatement: item?.entity_data?.claimStatement || '',
    justification: item?.entity_data?.justification || '',
    riskScenario: item?.entity_data?.riskScenario || '',
    goalRefs: item?.entity_data?.goalRefs || [],
  }),
  toPayload: (v) => ({
    name: v.claimStatement.substring(0, 100),
    description: v.justification,
    entity_type: 'claim',
    entity_data: {
      claimStatement: v.claimStatement,
      justification: v.justification,
      riskScenario: v.riskScenario,
      goalRefs: v.goalRefs,
    },
  }),
};

const CONTROL_SPEC = {
  label: 'Control',
  fields: (refs) => [
    {
      name: 'name',
      type: 'text',
      label: 'Control Name',
      required: true,
      autoFocus: true,
      placeholder: 'e.g., AES-256 Encryption for CAN messages',
    },
    { name: 'controlId',   type: 'text',     label: 'Control ID',  placeholder: 'e.g., CTRL-001', width: 'half' },
    { name: 'controlType', type: 'select',   label: 'Control Type', required: true, width: 'half', options: enumOptions(CONTROL_TYPES), defaultValue: 'Cryptographic' },
    { name: 'description', type: 'textarea', label: 'Description',  rows: 3 },
    {
      name: 'goalRefs',
      type: 'multi-select',
      label: 'Goal References',
      options: refOptions(refs.goals, (r) => r.name || r.entity_data?.goalStatement),
    },
    {
      name: 'threatRefs',
      type: 'multi-select',
      label: 'Threat References',
      options: refOptions(refs.threats),
    },
    {
      name: 'technologyRefs',
      type: 'multi-select',
      label: 'Technology References',
      options: refOptions(refs.technologies),
    },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    controlId: item?.entity_data?.controlId || '',
    controlType: item?.entity_data?.controlType || 'Cryptographic',
    goalRefs: item?.entity_data?.goalRefs || [],
    threatRefs: item?.entity_data?.threatRefs || [],
    technologyRefs: item?.entity_data?.technologyRefs || [],
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'control',
    entity_data: {
      controlId: v.controlId,
      controlType: v.controlType,
      goalRefs: v.goalRefs,
      threatRefs: v.threatRefs,
      technologyRefs: v.technologyRefs,
    },
  }),
};

const TECHNOLOGY_SPEC = {
  label: 'Technology',
  fields: () => [
    {
      name: 'name',
      type: 'text',
      label: 'Technology Name',
      required: true,
      autoFocus: true,
      placeholder: 'e.g., TCP/IP',
    },
    { name: 'technologyId', type: 'text', label: 'Technology ID', placeholder: 'e.g., TECH-001' },
    {
      name: 'description',
      type: 'textarea',
      label: 'Description',
      rows: 3,
      placeholder: 'e.g., Family of Internet Protocols',
    },
  ],
  initialValues: (item) => ({
    name: item?.name || '',
    description: item?.description || '',
    technologyId: item?.entity_data?.technologyId || '',
  }),
  toPayload: (v) => ({
    name: v.name,
    description: v.description,
    entity_type: 'technology',
    entity_data: {
      technologyId: v.technologyId,
    },
  }),
};

export const ENTITY_FORM_SPECS = {
  module: MODULE_SPEC,
  feature: FEATURE_SPEC,
  asset: ASSET_SPEC,
  damage_scenario: DAMAGE_SCENARIO_SPEC,
  attack_path: ATTACK_PATH_SPEC,
  goal: GOAL_SPEC,
  claim: CLAIM_SPEC,
  control: CONTROL_SPEC,
  technology: TECHNOLOGY_SPEC,
  threat: THREAT_SPEC,
};

export const isFormModalEntity = (entityType) =>
  Object.prototype.hasOwnProperty.call(ENTITY_FORM_SPECS, entityType);
